import type { PropsWithChildren, RefObject } from 'react'
import { useRef } from 'react'

import { nanoid } from 'nanoid'

import { useReturnFocus } from './useReturnFocus'
import { type IModalProps, Modal } from '../Modal/Modal'

export interface IModalDialog {
  open: IModalProps['open']
  title: string
  maxWidth?: IModalProps['maxWidth']
  onClose?: IModalProps['onClose']
  /**
   * Where focus goes when the dialog closes. Pass it when the control that
   * opened the dialog is gone by then: a menu item, a row the action removes,
   * a trigger that a tab switch unmounts. Focus moves without scrolling, and
   * only if the element is still in the document.
   *
   * Without it, focus returns to the element that had focus when the dialog
   * opened. Pass it from the first render: whether the dialog uses it is fixed
   * when the dialog mounts. The ref itself may change, and the one passed when
   * the dialog closes is used.
   *
   * A hand-off keeps focus with the next dialog: when a ModalDialog or
   * ConfirmationModal opens in the same update as this dialog closes, focus
   * is left for it instead of passing through this element first.
   */
  returnFocusRef?: RefObject<HTMLElement>
}

export const ModalDialog = ({
  open,
  title,
  maxWidth = 'md',
  children,
  onClose,
  returnFocusRef,
}: PropsWithChildren<IModalDialog>) => {
  const idRef = useRef<string>(nanoid())
  const disableRestoreFocus = useReturnFocus(open, returnFocusRef)

  // disableEnforceFocus lets portaled pickers (date pickers, select menus)
  // hold focus outside the dialog; cycleFocus keeps Tab inside it instead.
  return (
    <Modal
      aria-labelledby={idRef.current}
      maxWidth={maxWidth}
      open={open}
      onClose={onClose}
      disableEnforceFocus
      disableRestoreFocus={disableRestoreFocus}
    >
      <Modal.Card cycleFocus>
        <Modal.Header id={idRef.current} onClose={onClose}>
          {title}
        </Modal.Header>
        <Modal.Content>{children}</Modal.Content>
      </Modal.Card>
    </Modal>
  )
}
